import React, { JSX } from "react";
import Head from "next/head";

import { assetsRoot } from "config";
import { CommerceProduct, getCommerceProduct } from "constants/commerce";
import {
  CANONICAL_URL_BASE,
  META_DEFAULT_OG_IMAGE,
  META_DESCRIPTION,
  META_TITLE,
} from "constants/meta";

const POPPI_ASSETS = `${assetsRoot}/poppi`;

interface StaticRouteMetadata {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
}

export interface RouteSeoMetadata extends StaticRouteMetadata {
  path: string;
  type: "product" | "website";
  url: string;
  productJsonLd?: Record<string, unknown>;
}

export interface RouteSeoProps {
  asPath: string;
  pathname: string;
  pageProps?: unknown;
}

const STATIC_ROUTE_METADATA: Readonly<Record<string, StaticRouteMetadata>> = {
  "/": {
    title: META_TITLE,
    description: META_DESCRIPTION,
    image: META_DEFAULT_OG_IMAGE,
    imageAlt: "poppi prebiotic soda",
  },
  "/404": {
    title: "Page not found – poppi",
    description:
      "That page did not pop up. Explore poppi prebiotic soda flavors and variety packs.",
    image: META_DEFAULT_OG_IMAGE,
    imageAlt: "poppi prebiotic soda",
  },
  "/blogs/news": {
    title: "Refreshing Reads – poppi",
    description:
      "Refreshing reads, news, and stories from poppi, the prebiotic soda.",
    image: `${POPPI_ASSETS}/our-story/life-0.jpg`,
    imageAlt: "poppi stories and news",
  },
  "/cart": {
    title: "Cart – poppi",
    description: "Review the poppi products in your shopping cart.",
    image: META_DEFAULT_OG_IMAGE,
    imageAlt: "poppi prebiotic soda",
  },
  "/collections/drinks": {
    title: "Drinks – poppi",
    description:
      "Shop every poppi prebiotic soda flavor and find your new favorite.",
    image: `${POPPI_ASSETS}/sour-apple/can.png`,
    imageAlt: "poppi prebiotic soda flavors",
  },
  "/collections/variety-packs": {
    title: "Variety Packs – poppi",
    description:
      "Mix it up with poppi prebiotic soda variety packs featuring fan-favorite flavors.",
    image: `${POPPI_ASSETS}/variety/major-mix.png`,
    imageAlt: "poppi soda variety packs",
  },
  "/forgot-password": {
    title: "Restore Password – poppi",
    description: "Restore access to your poppi account.",
    image: META_DEFAULT_OG_IMAGE,
    imageAlt: "poppi prebiotic soda",
  },
  "/our-mission": {
    title: "Our Mission – poppi",
    description:
      "Learn how poppi is giving soda lovers bold flavor with ingredients they can love.",
    image: `${POPPI_ASSETS}/our-story/timeline-2025-poppi-joins-the-pepsico-fami.jpg`,
    imageAlt: "the poppi story and mission",
  },
  "/pages/brandnewdaysweeps": {
    title: "Official Rules – poppi",
    description:
      "Official rules for the 2026 poppi x Spider-Man: Brand New Day promotion.",
    image: `${POPPI_ASSETS}/legal/legal-hero.png`,
    imageAlt: "poppi official promotion rules",
  },
  "/pages/contact-us": {
    title: "Get in Touch – poppi",
    description: "Contact the poppi team.",
    image: META_DEFAULT_OG_IMAGE,
    imageAlt: "contact poppi",
  },
  "/pages/find-poppi": {
    title: "Find Poppi – poppi",
    description:
      "Find poppi prebiotic soda at a store near you or shop online.",
    image: `${POPPI_ASSETS}/find-poppi/wave-hero.png`,
    imageAlt: "find poppi at a retailer near you",
  },
  "/pages/legal": {
    title: "Legal – poppi",
    description: "Read the poppi terms of use.",
    image: `${POPPI_ASSETS}/legal/legal-hero.png`,
    imageAlt: "poppi legal information",
  },
  "/pages/love-island": {
    title: "Love Island – poppi",
    description:
      "Guess the poppi flavor bombshell of the week for your chance to win.",
    image: `${POPPI_ASSETS}/love-island/bg-main-desktop.jpg`,
    imageAlt: "poppi x Love Island",
  },
  "/pages/our-story": {
    title: "Our Story – poppi",
    description:
      "From a Dallas farmers market to shelves nationwide, discover the story behind poppi.",
    image: `${POPPI_ASSETS}/our-story/timeline-2025-poppi-joins-the-pepsico-fami.jpg`,
    imageAlt: "the story behind poppi",
  },
  "/pages/pop-up-terms-conditions": {
    title: "Event Terms & Conditions – poppi",
    description:
      "Read poppi event terms and conditions, including image and likeness terms.",
    image: `${POPPI_ASSETS}/legal/legal-hero.png`,
    imageAlt: "poppi event terms and conditions",
  },
  "/pages/poppi-desert-festival-kickback-2025": {
    title: "Poppi Desert Festival Kickback 2025",
    description:
      "Terms and conditions for the poppi Desert Festival Kickback 2025.",
    image: `${POPPI_ASSETS}/legal/legal-hero.png`,
    imageAlt: "poppi Desert Festival Kickback terms",
  },
  "/pages/poppi-university": {
    title: "Poppi University",
    description:
      "Join poppi University and bring events, content, and flavor to your campus.",
    image: `${POPPI_ASSETS}/university/marquee/IMG_7124_2.jpg.png`,
    imageAlt: "poppi University campus ambassadors",
  },
  "/pages/privacy-policy": {
    title: "Privacy Policy – poppi",
    description: "Read the poppi privacy policy.",
    image: `${POPPI_ASSETS}/legal/privacy-hero.png`,
    imageAlt: "poppi privacy policy",
  },
  "/pages/social-media-giveaway-terms-conditions": {
    title: "Giveaway Terms & Conditions – poppi",
    description:
      "Read the terms and conditions for poppi social media giveaways.",
    image: `${POPPI_ASSETS}/legal/legal-hero.png`,
    imageAlt: "poppi giveaway terms and conditions",
  },
  "/pages/sour-apple-terms-conditions": {
    title: "Sour Apple Terms & Conditions – poppi",
    description:
      "Read the terms and conditions for the poppi Sour Apple promotion.",
    image: `${POPPI_ASSETS}/sour-apple/can.png`,
    imageAlt: "poppi Sour Apple promotion",
  },
  "/pages/summer": {
    title: "The Taste of Summer – poppi",
    description: "Build your dream summer basket with poppi.",
    image: `${POPPI_ASSETS}/summer/video-poster-desktop.jpg`,
    imageAlt: "the taste of summer with poppi",
  },
  "/pages/vibes": {
    title: "Vibes – poppi",
    description: "Get into the vibes with poppi.",
    image: `${POPPI_ASSETS}/vibes/cans-desktop-3600w.png`,
    imageAlt: "get into the vibes with poppi",
  },
  "/pages/whypoppi": {
    title: "Why Poppi? – poppi",
    description:
      "Learn about poppi ingredients, prebiotics, nutrition, and frequently asked questions.",
    image: META_DEFAULT_OG_IMAGE,
    imageAlt: "why poppi prebiotic soda",
  },
  "/sign-in": {
    title: "Sign In – poppi",
    description: "Sign in to your poppi account.",
    image: META_DEFAULT_OG_IMAGE,
    imageAlt: "poppi prebiotic soda",
  },
  "/sign-up": {
    title: "Sign Up – poppi",
    description: "Create a poppi account.",
    image: META_DEFAULT_OG_IMAGE,
    imageAlt: "poppi prebiotic soda",
  },
  "/terms": {
    title: "Terms of Service – poppi",
    description: "Read the poppi terms of service.",
    image: `${POPPI_ASSETS}/legal/legal-hero.png`,
    imageAlt: "poppi terms of service",
  },
};

const STATIC_ROUTE_METADATA_BY_PATH = new Map(
  Object.entries(STATIC_ROUTE_METADATA)
);

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
}

function readString(
  record: Record<string, unknown> | undefined,
  key: "description" | "handle" | "title"
): string | undefined {
  let value: unknown;
  switch (key) {
    case "description":
      value = record?.description;
      break;
    case "handle":
      value = record?.handle;
      break;
    case "title":
      value = record?.title;
      break;
  }
  return typeof value === "string" && value.trim() ? value : undefined;
}

function normalizePath(path: string): string {
  const withoutQuery = path.split(/[?#]/, 1)[0] || "/";
  const withLeadingSlash = withoutQuery.startsWith("/")
    ? withoutQuery
    : `/${withoutQuery}`;
  if (withLeadingSlash === "/") return withLeadingSlash;
  return withLeadingSlash.replace(/\/+$/, "") || "/";
}

function decodePathSegment(segment: string | undefined): string | undefined {
  if (!segment) return undefined;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function routeHandle(path: string): string | undefined {
  return decodePathSegment(path.match(/^\/products\/([^/]+)$/)?.[1]);
}

function siteUrl(path: string): string {
  const base = CANONICAL_URL_BASE.replace(/\/+$/, "");
  return path === "/" ? base : `${base}${path}`;
}

function humanizePathSegment(segment: string): string {
  return segment
    .split("-")
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

function gtinProperty(upc: string): Record<string, string> {
  if (!/^\d+$/.test(upc) || ![8, 12, 13, 14].includes(upc.length)) {
    return {};
  }

  const digits = [...upc].map(Number);
  const checkDigit = digits.pop();
  const weightedSum = digits
    .reverse()
    .reduce((sum, digit, index) => sum + digit * (index % 2 === 0 ? 3 : 1), 0);
  if ((10 - (weightedSum % 10)) % 10 !== checkDigit) return {};

  return { [`gtin${upc.length}`]: upc };
}

interface ProductSeoData {
  commerce: CommerceProduct;
  description: string;
  images: string[];
}

function resolveProductSeo(
  path: string,
  pageProps: unknown
): ProductSeoData | undefined {
  const props = asRecord(pageProps);
  const product = asRecord(props?.product);
  const productHandle = readString(product, "handle");
  const handle =
    productHandle ?? readString(props, "handle") ?? routeHandle(path);
  if (!handle) return undefined;

  const commerce = getCommerceProduct(handle);
  if (!commerce) return undefined;

  const productMatches = !productHandle || productHandle === commerce.handle;
  const description = productMatches
    ? readString(product, "description")
    : undefined;
  const rawGallery = productMatches ? product?.gallery : undefined;
  const images = Array.isArray(rawGallery)
    ? rawGallery.filter(
        (image): image is string => typeof image === "string" && Boolean(image)
      )
    : [];

  return {
    commerce,
    description:
      description ??
      `Shop ${commerce.name} ${commerce.variant} from poppi, the prebiotic soda.`,
    images: images.length ? images : [META_DEFAULT_OG_IMAGE],
  };
}

function productStructuredData(
  product: ProductSeoData,
  url: string
): Record<string, unknown> {
  const { commerce, description, images } = product;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${url}#product`,
    name: commerce.name,
    description,
    image: images,
    url,
    ...(commerce.sku ? { sku: commerce.sku } : {}),
    productID: commerce.upc,
    category: commerce.type,
    ...gtinProperty(commerce.upc),
    brand: {
      "@type": "Brand",
      name: META_TITLE,
    },
  };
}

function categoryMetadata(
  path: string,
  pageProps: unknown
): StaticRouteMetadata | undefined {
  if (!path.startsWith("/category/")) return undefined;
  const props = asRecord(pageProps);
  const pathLabel = humanizePathSegment(path.split("/").pop() || "Products");
  const categoryTitle = readString(props, "title") ?? pathLabel;
  return {
    title: `${categoryTitle} – poppi`,
    description: `Shop ${categoryTitle.toLowerCase()} from poppi.`,
    image: META_DEFAULT_OG_IMAGE,
    imageAlt: `${categoryTitle} from poppi`,
  };
}

function fallbackMetadata(path: string): StaticRouteMetadata {
  const lastSegment = path.split("/").filter(Boolean).pop();
  const label = lastSegment ? humanizePathSegment(lastSegment) : META_TITLE;
  return {
    title: `${label} – poppi`,
    description: `Explore ${label.toLowerCase()} from poppi, the prebiotic soda.`,
    image: META_DEFAULT_OG_IMAGE,
    imageAlt: `${label} from poppi`,
  };
}

export function resolveRouteSeo({
  asPath,
  pathname,
  pageProps,
}: RouteSeoProps): RouteSeoMetadata {
  const path = normalizePath(asPath || pathname || "/");
  const product = resolveProductSeo(path, pageProps);

  if (product) {
    const productPath = `/products/${product.commerce.handle}`;
    const url = siteUrl(productPath);
    return {
      path: productPath,
      title: `${product.commerce.name} – poppi`,
      description: product.description,
      image: product.images[0],
      imageAlt: `${product.commerce.name} ${product.commerce.variant} from poppi`,
      type: "product",
      url,
      productJsonLd: productStructuredData(product, url),
    };
  }

  const normalizedPattern = normalizePath(pathname || path);
  const metadata =
    STATIC_ROUTE_METADATA_BY_PATH.get(path) ??
    STATIC_ROUTE_METADATA_BY_PATH.get(normalizedPattern) ??
    categoryMetadata(path, pageProps) ??
    fallbackMetadata(path);

  return {
    ...metadata,
    path,
    type: "website",
    url: siteUrl(path),
  };
}

export function serializeJsonLd(value: unknown): string {
  return JSON.stringify(value).replace(
    /[<>&\u2028\u2029]/g,
    (character) => `\\u${character.charCodeAt(0).toString(16).padStart(4, "0")}`
  );
}

export function RouteSeo(props: RouteSeoProps): JSX.Element {
  const metadata = resolveRouteSeo(props);

  return (
    <Head>
      <meta property="og:site_name" content={META_TITLE} key="og:site_name" />
      <meta property="og:title" content={metadata.title} key="og:title" />
      <meta
        property="og:description"
        content={metadata.description}
        key="og:description"
      />
      <meta property="og:image" content={metadata.image} key="og:image" />
      <meta
        property="og:image:alt"
        content={metadata.imageAlt}
        key="og:image:alt"
      />
      <meta property="og:type" content={metadata.type} key="og:type" />
      <meta property="og:url" content={metadata.url} key="og:url" />

      <meta
        name="twitter:card"
        content="summary_large_image"
        key="twitter:card"
      />
      <meta name="twitter:title" content={metadata.title} key="twitter:title" />
      <meta
        name="twitter:description"
        content={metadata.description}
        key="twitter:description"
      />
      <meta name="twitter:image" content={metadata.image} key="twitter:image" />
      <meta
        name="twitter:image:alt"
        content={metadata.imageAlt}
        key="twitter:image:alt"
      />

      {metadata.productJsonLd && (
        <script id="product-structured-data" type="application/ld+json">
          {serializeJsonLd(metadata.productJsonLd)}
        </script>
      )}
    </Head>
  );
}
